import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { BrandLockup } from './BrandLockup';

interface AuthSplitLayoutProps {
  /** Left column — dark hero panel. Hidden on mobile. */
  hero?: React.ReactNode;
  /** Right column — sign-in form (or mobile full-page). */
  children: React.ReactNode;
}

/**
 * Split layout: dark hero left (≈51 %), form right (≈49 %).
 * On mobile (< md) collapses to a single-column centred form.
 *
 * The right panel supplies padding + vertical centring; callers
 * only provide the form content at a constrained max-width.
 */
export function AuthSplitLayout({ hero, children }: AuthSplitLayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <BrandLockup size={30} />
        </Box>
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1.05fr 1fr',
        minHeight: '100vh',
      }}
    >
      {/*
       * Form is FIRST in DOM so keyboard tab order reaches inputs before hero dots.
       * gridColumn pins it visually to the right column regardless of DOM position.
       */}
      <Box
        component="main"
        sx={{
          gridColumn: 2,
          gridRow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          px: 8,     // 64px
          py: '52px',
        }}
      >
        {children}
      </Box>

      {/* Hero is SECOND in DOM (tabs after form) but pinned visually to column 1. */}
      <Box sx={{ gridColumn: 1, gridRow: 1, position: 'relative', overflow: 'hidden' }}>
        {hero}
      </Box>
    </Box>
  );
}
